/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { useMemo, useState } from 'react';
import {
  Modal,
  Input,
  Typography,
  DatePicker,
  Select,
  Tag,
  Form,
  message,
} from 'antd';
import { useIntl } from 'react-intl';
import { ModalProps } from 'antd/lib/modal';
import moment from 'moment';
import isEmail from 'validator/es/lib/isEmail';
import { DatePickerProps } from 'antd/lib/date-picker';
import { uniq } from 'lodash';
import { SelectProps } from 'antd/lib/select';

import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import { DbFile } from '~src/types/db';
import { ModalTitle } from '~components/_common/ModalTitle/ModalTitle';
import { fadeInKeyframes } from '~src/styles/animations';
import { __DEV__ } from '~src/config';
import { useMutation } from 'react-query';
import { shareTender } from '~src/services/api/tender';
import { useProjectId } from '~src/hooks/useProjectId';

const hours = [...new Array(24)].map((v, index) => index);
/** @returns moment(tomorrow at 17:30) */
const getDefaultDateVal = () =>
  moment().add(14, 'day').set('hours', 23).set('minutes', 59).set('seconds', 0);

const getDefaultEmails = () => (__DEV__ ? [] : []);

export interface ShareTenderModalProps extends Partial<ModalProps> {
  files: DbFile[];
  tenderId: string;
  onDone?: () => void;
}
export const ShareTenderModal: React.FC<ShareTenderModalProps> = ({
  files,
  onCancel,
  onDone,
  tenderId,
  ...modalProps
}) => {
  const projectId = useProjectId();
  const intl = useIntl();
  const styles = useStyles(styleFn);
  const [emails, setEmails] = useState<string[]>(getDefaultEmails);
  const [disableButton, setDisableButton] = useState(false);

  const [doShareTender, {}] = useMutation(shareTender, {
    onSuccess: (data) => {
      // queryCache.invalidateQueries('folders');
    },
    onError: (error, item) => {
      //message.error(intl.formatMessage({ id: 'folder-to-tender-failed' }));
    },
  });

  // throw if shown without files
  /*   if (modalProps.visible && !files.length)
    throw new Error('ShareTenderModal should be shown with files!'); */

  const invalidEmails = useMemo(() => emails.filter((val) => !isEmail(val)), [
    emails,
  ]);

  const isValid = () => {
    if (!emails.length || invalidEmails.length) return false;

    return true;
  };

  const handleSubmit = async () => {
    if (!isValid()) return;

    setDisableButton(true);

    doShareTender({
      tenderId,
      projectId: String(projectId),
      emails,
    })
      .then(
        () => onDone && onDone(),
        message.success(intl.formatMessage({ id: 'share-tender-success' }))
      )
      .catch((err) => setDisableButton(false));
  };

  const tagRender: SelectProps<string[]>['tagRender'] = (tagProps) => {
    const invalid = invalidEmails.includes(tagProps.value as string);
    return (
      <Tag
        css={styles.tag}
        color={invalid ? 'red' : undefined}
        title={invalid ? 'Invalid' : undefined}
        {...tagProps}>
        {tagProps.value}
      </Tag>
    );
  };

  return (
    <Modal
      title={
        <ModalTitle
          text={intl.formatMessage({ id: 'send-share-tender' })}
          icon={['far', 'share-square']}
        />
      }
      okText={intl.formatMessage({ id: 'send' })}
      cancelText={intl.formatMessage({ id: 'cancel' })}
      width={420}
      centered
      okButtonProps={{
        disabled: !isValid() || disableButton,
      }}
      onOk={handleSubmit}
      onCancel={onCancel}
      afterClose={() => {
        // reset state
        setDisableButton(false);
        setEmails(getDefaultEmails);
      }}
      {...modalProps}>
      <Typography.Paragraph css={styles.label} strong>
        {intl.formatMessage({ id: 'send-link-to' })}
      </Typography.Paragraph>
      <Select<string[]>
        mode="tags"
        style={{ width: '100%' }}
        placeholder={intl.formatMessage({ id: 'share-emails-placeholder' })}
        open={false}
        tokenSeparators={[',', ' ']}
        value={emails}
        onChange={(val) => setEmails(uniq(val))}
        tagRender={tagRender}></Select>
    </Modal>
  );
};

const styleFn: ThemedStyleFn = (theme) => ({
  label: {
    '&.ant-typography': {
      marginBottom: 5,
    },
  },
  tag: {
    borderRadius: 4,
    marginRight: 3,
    fontSize: 14,
    animation: `${fadeInKeyframes} 0.2s ease-out`,
  },
});
